import { makeObservable, computed } from "mobx";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { SyncModelKind } from "@/store/sync/types";
import { WithAppStore } from "@/store/types";
import { SpaceAccountCalendarEventModelData } from "@/store/calendar/space-account-calendar-event/types";
import { SyncUpdateValue } from "@/store/sync/types";
import { SpaceAccountCalendarObservable } from "@/store/calendar/space-account-calendar/SpaceAccountCalendarObservable";
import { SpaceAccountCalendarConnectionObservable } from "@/store/calendar/space-account-calendar-connection/SpaceAccountCalendarConnectionObservable";
import { resolveSourceIdForCalendarEvent } from "@/modules/uuid/sync-models/resolveSourceIdForCalendarEvent";
import { SourceObservable } from "@/store/sources/SourceObservable";
import { NoteObservable } from "@/store/note/NoteObservable";
import { DateTime } from "luxon";
import { getMeetingPlatformFromUrl } from "@/store/calendar/space-account-calendar-event/helpers";
import { formatCalendarEventTime } from "@/domains/calendar/formating";
import { resolveDefaultCalendarEventTemplateSyncModelUuid } from "@/modules/uuid/sync-models/resolveDefaultCalendarEventTemplateSyncModelUuid";
import { noteModule } from "@mem-labs/common-editor";
import { logger } from "@/modules/logger";

export class SpaceAccountCalendarEventObservable extends BaseSyncModel<SpaceAccountCalendarEventModelData> {
  public modelKind = SyncModelKind.SpaceAccountCalendarEvent;

  constructor({
    id,
    data,
    store,
  }: {
    id: string;
    data: SyncUpdateValue<SpaceAccountCalendarEventModelData>;
  } & WithAppStore) {
    super({ id, data, store });

    makeObservable(this, {
      modelKind: false,
      associatedSpaceAccountCalendarId: computed,
      associatedSpaceAccountCalendar: computed,
      associatedSpaceAccountCalendarConnection: computed,
      associatedConnectedCalendarEventId: computed,
      startTime: computed,
      startTimeLabel: computed,
      relativeStartTimeLabel: computed,
      endTime: computed,
      isAllDay: computed,
      primaryLabel: computed,
      url: computed,
      meetingPlatform: computed,
      associatedSource: computed,
      associatedNote: computed,
      calendarName: computed,
      calendarEmailAddress: computed,
      isUpcomingEvent: computed,
      isImminentEvent: computed,
      getTemplateIdAndEncodedContentAsync: false,
    });
  }

  get associatedSpaceAccountCalendarId() {
    return this.modelData.associated_space_account_calendar_id;
  }

  get associatedSpaceAccountCalendar(): SpaceAccountCalendarObservable | undefined {
    return this.store.spaceAccountCalendars.get(this.associatedSpaceAccountCalendarId);
  }

  get associatedSpaceAccountCalendarConnection():
    | SpaceAccountCalendarConnectionObservable
    | undefined {
    const associatedSpaceAccountCalendarConnectionId =
      this.associatedSpaceAccountCalendar?.associatedSpaceAccountCalendarConnectionId;
    if (!associatedSpaceAccountCalendarConnectionId) return undefined;
    return this.store.spaceAccountCalendarConnections.get(
      associatedSpaceAccountCalendarConnectionId
    );
  }

  get associatedConnectedCalendarEventId() {
    return this.modelData.associated_connected_calendar_event_id;
  }

  get startTime() {
    return this.modelData.start_time;
  }

  get startTimeLabel() {
    if (this.isAllDay) return "All day";
    if (!this.startTime) return "";
    return formatCalendarEventTime(this.startTime, null, this.isAllDay);
  }

  get relativeStartTimeLabel() {
    if (!this.startTime) return "";
    const now = DateTime.now();
    const startTime = DateTime.fromISO(this.startTime);
    const diff = Math.round(startTime.diff(now, "minutes").minutes);
    return diff > 0 ? `Starting in ${diff} min` : `Started ${Math.abs(diff)} min ago`;
  }

  get endTime() {
    return this.modelData.end_time;
  }

  get isAllDay() {
    return this.modelData.is_all_day;
  }

  get isUpcomingEvent() {
    if (!this.startTime) return false;
    const now = DateTime.now();
    const upcomingMeetingsStart = now.minus({ minutes: 15 });
    const upcomingMeetingsEnd = now.plus({ hours: 24 });
    const startTime = DateTime.fromISO(this.startTime);
    return startTime >= upcomingMeetingsStart && startTime <= upcomingMeetingsEnd;
  }

  get isImminentEvent() {
    if (!this.startTime) return false;
    const now = DateTime.now();
    const imminentMeetingsStart = now.minus({ minutes: 5 });
    const imminentMeetingsEnd = now.plus({ minutes: 5 });
    const startTime = DateTime.fromISO(this.startTime);
    return startTime >= imminentMeetingsStart && startTime <= imminentMeetingsEnd;
  }

  get primaryLabel() {
    return this.modelData.primary_label;
  }

  get url() {
    return this.modelData.meet_link;
  }

  get meetingPlatform(): string {
    return getMeetingPlatformFromUrl(this.url);
  }

  get associatedSource(): SourceObservable | undefined {
    const sourceId = resolveSourceIdForCalendarEvent({
      connectedCalendarEventId: this.associatedConnectedCalendarEventId,
    });
    return this.store.sources.get(sourceId);
  }

  get associatedNote(): NoteObservable | undefined {
    return this.associatedSource?.associatedNote;
  }

  get calendarName() {
    return this.associatedSpaceAccountCalendar?.primaryLabel;
  }

  get calendarEmailAddress() {
    return this.associatedSpaceAccountCalendar?.associatedSpaceAccountCalendarConnection
      ?.associatedEmailAddress;
  }

  async getTemplateIdAndEncodedContentAsync(): Promise<
    { templateId: string; encodedContent: string } | undefined
  > {
    const calendarEventId = this.id;
    const templateId = resolveDefaultCalendarEventTemplateSyncModelUuid({
      spaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
    });
    const template = await this.store.templates.getAsync(templateId);
    if (!template) {
      logger.error({
        message:
          "[SpaceAccountCalendarEventObservable][getTemplateIdAndEncodedContentAsync] Error creating new note using template. Missing template",
        info: { templateId, calendarEventId },
      });
      return undefined;
    }

    const templateContents = await template.getTemplateContentsForInsertionAsync();
    if (!templateContents) {
      logger.error({
        message:
          "[SpaceAccountCalendarEventObservable][getTemplateIdAndEncodedContentAsync] Error creating new note using template. Missing remote content",
        info: { templateId, calendarEventId },
      });
      return undefined;
    }

    const { remoteContent, documentUpdates } = templateContents;
    const variables = Object.fromEntries(
      Object.entries(this.store.templates.getVariables(this)).map(([code, { value }]) => [
        code,
        value,
      ])
    );
    const encodedContent = noteModule.replaceTemplateVariablesInEncodedContent({
      encodedContent: remoteContent,
      documentUpdates,
      stringifiedValues: JSON.stringify(variables),
      enableLogging: true,
    });

    if (!encodedContent) {
      logger.error({
        message:
          "[SpaceAccountCalendarEventObservable][getTemplateIdAndEncodedContentAsync] Error creating new note using template. Missing encoded content",
        info: { templateId, calendarEventId },
      });
      return undefined;
    }

    return { templateId, encodedContent };
  }
}
